import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Loader, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [partnerId, setPartnerId] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError('Kein Token gefunden');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Verifizierung fehlgeschlagen');
      }

      setStatus('success');
      setPartnerId(data.partner_id);
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const handleResendLink = async () => {
    setResending(true);
    setResendSuccess(false);
    
    // Extract email from URL or ask user
    const email = searchParams.get('email') || prompt('Bitte geben Sie Ihre E-Mail-Adresse ein:');
    
    if (!email) {
      setResending(false);
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setResendSuccess(true);
      }
    } catch (err) {
      // Silent fail - don't expose errors
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {status === 'verifying' && (
          <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center shadow-lg">
            <Loader className="w-16 h-16 text-brand-green mx-auto mb-6 animate-spin" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              E-Mail wird verifiziert...
            </h1>
            <p className="text-gray-600">Bitte einen Moment Geduld</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white border border-gray-200 rounded-3xl p-12 text-center shadow-lg">
            <div className="w-20 h-20 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-brand-green/15">
              <Check className="w-10 h-10 text-brand-green" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              E-Mail bestätigt!
            </h1>
            <p className="text-xl text-gray-600 mb-10">
              Vervollständigen Sie jetzt Ihr Profil im Onboarding
            </p>
            <Button
              onClick={() => navigate(`/portal/onboarding?partner_id=${partnerId}`)}
              size="lg"
              className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-2xl font-semibold"
            >
              Weiter zum Onboarding
              <ArrowRight className="w-5 h-5 ml-3" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white border border-red-200 rounded-3xl p-12 text-center shadow-lg">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Verifizierung fehlgeschlagen
            </h1>
            <p className="text-red-600 mb-6">{error}</p>
            
            {resendSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-green-700">
                  Ein neuer Bestätigungslink wurde versendet. Bitte prüfen Sie Ihr E-Mail-Postfach.
                </p>
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleResendLink}
                disabled={resending}
                className="bg-brand-green hover:bg-brand-green-dark text-white"
              >
                {resending ? 'Wird gesendet...' : 'Link erneut senden'}
              </Button>
              
              <Button
                onClick={() => navigate('/partner/registrieren')}
                variant="outline"
                className="border-2 border-gray-900"
              >
                Zurück zur Registrierung
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;